import { useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Tag,
  Spin,
  Tooltip,
} from "antd";
import { ReactComponent as Call } from "../../assets/svg/icons/call.svg";
import { ReactComponent as ViewEmail } from "../../assets/svg/icons/email.svg";
import { ReactComponent as Meeting } from "../../assets/svg/icons/meeting.svg";
import { ReactComponent as Followup } from "../../assets/svg/icons/followup.svg";
import { ReactComponent as Whatsapp } from "../../assets/svg/icons/whatsapp.svg";
import { ReactComponent as Chat } from "../../assets/svg/icons/chat.svg";
import { useDispatch, useSelector } from "react-redux";
import {
  getLeadRemarkChips,
  submitLeadRemarks,
} from "../../actionCreator/leadDetailsActionCreator";
import dayjs from "dayjs";

const { TextArea } = Input;

const TASK_TYPE_API_MAP = {
  call: "Call Task",
  email: "Email Task",
  meeting: "Meeting Task",
  followup: "Follow-up Task",
  chat: "Chat Task",
  whatsapp: "WhatsApp Task",
};

const TaskForm = ({
  task,
  setTask,
  setActiveTab,
  initialValues,
  handleClose,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [chipsLoading, setChipsLoading] = useState(false);

  const leadDetail = useSelector(
    (state) => state.LeadDetailsReducer.leadDetails,
  );
  const leadTasksStatusList = useSelector(
    (state) => state.LeadDetailsReducer.leadTasksStatusList,
  );
  const leadRemarkChips = useSelector(
    (state) => state.LeadDetailsReducer.leadRemarkChips,
  );
  const selectedTaskType = Form.useWatch("type", form);
  const selectedStatus = Form.useWatch("status", form);

  const statusOptions = useMemo(() => {
    if (Array.isArray(leadTasksStatusList) && leadTasksStatusList.length) {
      return leadTasksStatusList
        .map((item) => {
          if (typeof item === "string") {
            const normalizedValue = item.trim();
            return {
              value: normalizedValue,
              label: normalizedValue,
            };
          }

          const value =
            item?.value || item?.status || item?.name || item?.label || "";
          const label =
            item?.label || item?.name || item?.status || item?.value || "";

          if (!value || !label) return null;

          return {
            value: String(value).trim(),
            label: String(label).trim(),
          };
        })
        .filter(Boolean);
    }

    return [
      { value: "open", label: "Open" },
      { value: "re-scheduled", label: "Re-scheduled" },
      { value: "cancelled", label: "Cancelled" },
      { value: "completed", label: "Completed" },
    ];
  }, [leadTasksStatusList]);

  const normalizedRemarkChips = useMemo(() => {
    if (!Array.isArray(leadRemarkChips)) return [];

    return leadRemarkChips
      .map((chip) => {
        if (typeof chip === "string") {
          return chip.trim();
        }

        return String(
          chip?.label ||
            chip?.name ||
            chip?.title ||
            chip?.remarks ||
            chip?.value ||
            "",
        ).trim();
      })
      .filter(Boolean);
  }, [leadRemarkChips]);

  const handleChipClick = (chipText) => {
    const currentRemark = form.getFieldValue("remarks") || "";
    const nextRemark = currentRemark?.trim()
      ? `${currentRemark} ${chipText}`.trim()
      : chipText;

    form.setFieldsValue({ remarks: nextRemark });
  };

  const onFinish = (values) => {
    const isUpdate = !!initialValues?.id;
    const currentTaskType = values.type || task;
    const payload = {
      ...values,
      type: currentTaskType,
      interactionDate: values.interactionDate
        ? values.interactionDate.toISOString()
        : null,
      isContactBackRequired: false,
      isContacted: false,
      isToDisplay: values.status === "open",
      lead: leadDetail,
      update: isUpdate,
      id: initialValues?.id || "",
    };

    dispatch(submitLeadRemarks(payload));
    form.resetFields();
    setTask();
    handleClose();
  };

  const onCancel = () => {
    form.resetFields();
    setTask();
    handleClose();
  };

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        type: initialValues?.type || initialValues?.title,
        title: initialValues?.title,
        interactionDate: dayjs(initialValues?.interactionDate),
      });
      setTask(initialValues?.type || initialValues?.title);
    }
  }, [initialValues, form, setTask]);

  useEffect(() => {
    const normalizedType = (selectedTaskType || task || "").trim();
    const normalizedStatus = (selectedStatus || "").trim();
    const apiTaskType = TASK_TYPE_API_MAP[normalizedType] || normalizedType;

    if (!apiTaskType || !normalizedStatus) {
      return;
    }

    setChipsLoading(true);
    dispatch(
      getLeadRemarkChips({
        type: apiTaskType,
        status: normalizedStatus,
      }),
    ).finally(() => {
      setChipsLoading(false);
    });
  }, [dispatch, selectedStatus, selectedTaskType, task]);

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        autoComplete="off"
        requiredMark={false}
        className=""
      >
        <div className="tw-p-6">
          <Row gutter={[24, 0]}>
            <Col span={24}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Select Task<span className="tw-text-red-500 tw-ml-0.5">*</span>
                  </span>
                }
                name="type"
                className="mb15"
                rules={[{ required: true, message: "Please select task" }]}
              >
                <div className="tw-flex tw-flex-wrap tw-gap-2.5">
                  <Tooltip title="Call">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "call"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("call");
                        form.setFieldsValue({ type: "call" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <Call />
                      </div>
                      <span>Call</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Email">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "email"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("email");
                        form.setFieldsValue({ type: "email" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <ViewEmail />
                      </div>
                      <span>Email</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Meeting">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "meeting"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("meeting");
                        form.setFieldsValue({ type: "meeting" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <Meeting />
                      </div>
                      <span>Meeting</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Follow-up">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "followup"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("followup");
                        form.setFieldsValue({ type: "followup" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <Followup />
                      </div>
                      <span>Follow-up</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="Chat">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "chat"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("chat");
                        form.setFieldsValue({ type: "chat" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <Chat />
                      </div>
                      <span>Chat</span>
                    </div>
                  </Tooltip>
                  <Tooltip title="WhatsApp">
                    <div
                      className={`tw-flex tw-items-center tw-gap-1.5 tw-px-3.5 tw-py-2 tw-rounded-xl tw-text-xs tw-font-semibold tw-cursor-pointer tw-border tw-border-solid tw-transition-all tw-duration-200 ${
                        task === "whatsapp"
                          ? "tw-bg-theme tw-text-white tw-border-theme tw-shadow-[0_4px_12px_rgba(93,68,255,0.2)]"
                          : "tw-bg-slate-50 tw-text-slate-600 tw-border-slate-200/80 hover:tw-bg-slate-100 hover:tw-text-slate-800"
                      }`}
                      onClick={() => {
                        setTask("whatsapp");
                        form.setFieldsValue({ type: "whatsapp" });
                      }}
                    >
                      <div className="tw-w-3.5 tw-h-3.5 tw-flex tw-items-center">
                        <Whatsapp />
                      </div>
                      <span>WhatsApp</span>
                    </div>
                  </Tooltip>
                </div>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Status<span className="tw-text-red-500 tw-ml-0.5">*</span>
                  </span>
                }
                name="status"
                className="mb15"
                rules={[{ required: true, message: "Please select status" }]}
              >
                <Select
                  placeholder="Select status"
                  options={statusOptions}
                  className="tw-w-full !tw-h-[38px] !tw-rounded-lg"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Date & Time<span className="tw-text-red-500 tw-ml-0.5">*</span>
                  </span>
                }
                name="interactionDate"
                className="mb15"
                rules={[
                  { required: true, message: "Please select interaction date" },
                ]}
              >
                <DatePicker
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  placeholder="Pick date and time"
                  className="tw-w-full !tw-h-[38px] !tw-rounded-lg !tw-border-slate-200"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              {chipsLoading ? (
                <div className="tw-flex tw-justify-center tw-py-4">
                  <Spin size="small" />
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-gap-2.5 tw-mb-5">
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Suggestions
                  </span>
                  {normalizedRemarkChips.length > 0 ? (
                    <div className="tw-flex tw-flex-wrap tw-gap-2">
                      {normalizedRemarkChips.map((chip) => (
                        <Tag
                          key={chip}
                          className="tw-cursor-pointer tw-rounded-full tw-border tw-border-solid tw-border-border-secondary tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-text-primary hover:tw-border-theme hover:tw-text-theme"
                          onClick={() => handleChipClick(chip)}
                        >
                          {chip}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    <div className="tw-text-xs tw-text-sub-text">
                      No suggestion chips available for this task type and status.
                    </div>
                  )}
                </div>
              )}

              <Form.Item
                label={
                  <span className="tw-text-xs tw-font-bold tw-text-slate-600">
                    Lead Remark<span className="tw-text-red-500 tw-ml-0.5">*</span>
                  </span>
                }
                name="remarks"
                className="mb15"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || !String(value).trim()) {
                        return Promise.reject(new Error("Please enter a remark"));
                      }

                      return Promise.resolve();
                    },
                  },
                  { max: 255, message: "Note can’t exceed 255 characters." },
                ]}
              >
                <TextArea
                  rows={5}
                  maxLength={500}
                  placeholder="Type your remark here…"
                  className="!tw-bg-amber-100/40 !tw-border-slate-200/80 focus:!tw-border-theme focus:!tw-shadow-[0_0_0_2px_rgba(99,102,241,0.1)] !tw-rounded-xl tw-p-3.5 tw-text-slate-700 placeholder:tw-text-slate-400 tw-transition-all"
                />
              </Form.Item>
            </Col>
          </Row>
        </div>

        <div className="tw-flex tw-bg-slate-50 tw-rounded-b-2xl tw-py-4 tw-px-6 tw-items-center tw-justify-end tw-gap-3 tw-border tw-border-solid tw-border-slate-100/50">
          <Button
            className="!tw-h-[38px] !tw-rounded-lg !tw-border-slate-200 !tw-bg-white !tw-px-5 !tw-text-xs !tw-font-bold !tw-text-slate-500 hover:!tw-border-slate-300 hover:!tw-text-slate-700 tw-transition-all tw-duration-200"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            className="theme-btn !tw-h-[38px] !tw-rounded-lg tw-font-bold tw-text-xs tw-shadow-[0_4px_12px_rgba(93,68,255,0.15)] hover:tw-shadow-[0_6px_16px_rgba(93,68,255,0.25)] hover:tw-scale-[1.01] tw-transition-all tw-duration-200"
          >
            {initialValues?.id ? "Edit Task" : "Create Task"}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default TaskForm;
